import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';
import PermissionRow from './PermissionRow';
import RoleHeader from './RoleHeader';
import Text from 'components/Text';
import Box from 'components/Box';
import colorTheme from 'constants/color';
import { EPermissionRole } from 'components/Permission/interface';
import { IPermissionConfigFeature, IPermissionConfigRes } from 'features/settingService/services/types/permission';
import { Table } from 'antd/lib';
import { ConfigProvider, message } from 'antd';
import VerifyPinRow from './VerifyPinRow';
import VerifyPinHeader from './VerifyPinHeader';
import Button from 'components/Button';
import { useAppDispatch } from 'store/hooks';
import settingServiceSelectors from 'features/settingService/services/selectors';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { AxiosResponse } from 'axios';
import settingApis from 'features/settingService/services/apis';
import settingServiceActions from 'features/settingService/services/actions';
import { useGetPermissionConfigFiltered } from 'features/settingService/services/hooks/useGetPermissionConfigFiltered';

const PermissionTable = () => {
  const [data] = useGetPermissionConfigFiltered();
  const dispatch = useAppDispatch();
  const setLoadingPage = useSetLoadingPage();
  const initData = settingServiceSelectors.getPermissionConfig();
  const dataChanged = settingServiceSelectors.getPermissionConfigChanged();

  const columns: ColumnsType<IPermissionConfigRes> = [
    {
      title: () => <Text variant='H9'>MODULE</Text>,
      key: 'moduleName',
      dataIndex:'moduleName',
      width: 120,
      fixed: 'left',
      render:(text) => 
        <Row p={'4'}>
          <Text variant='H9'>{text}</Text>
        </Row>
    },
    {
      title: () => <Text variant='H9'>FEATURE</Text>,
      dataIndex: 'features',
      key: 'features',
      width: 120,
      render: (features: IPermissionConfigFeature[]) => <PermissionWrapper>
          {features.map(feature => (
            <Row key={feature.featureCode} width={'100%'}>
              <Row pl='4'>
                <Text variant='CONTENT_2'>{feature.featureName}</Text>
              </Row>
            </Row>
          ))}
        </PermissionWrapper>,
    },
    {
      title: () => (<VerifyPinHeader label='PIN CODE'/>),
      key: 'verifyPin',
      align: 'center',
      width: 120,
      render: (_, record) => (<VerifyPinRow record={record} />),
    },
    {
      title: () => (<RoleHeader role={EPermissionRole.ADMIN} label='ADMIN'/>),
      key: EPermissionRole.ADMIN,
      align: 'center',
      width: 120,
      render: (_, record) => (<PermissionRow record={record} role={EPermissionRole.ADMIN} />),
    },
    {
      title: () => (<RoleHeader role={EPermissionRole.MANAGER} label='MANAGER'/>),
      key: EPermissionRole.MANAGER,
      align: 'center',
      width: 120,
      render: (_, record) => (<PermissionRow record={record} role={EPermissionRole.MANAGER} />),
    },
    {
      title: () => (<RoleHeader role={EPermissionRole.CASHIER} label='RECEPTIONIST'/>),
      key: EPermissionRole.CASHIER,
      align: 'center',
      width: 120,
      render: (_, record) => (<PermissionRow record={record} role={EPermissionRole.CASHIER} />),
    },
    {
      title: () => (<RoleHeader role={EPermissionRole.TECHNICAL} label='TECHNICIAN'/>),
      key: EPermissionRole.TECHNICAL,
      align: 'center',
      width: 120,
      render: (_, record) => (<PermissionRow record={record} role={EPermissionRole.TECHNICAL} />),
    },
    
  ];

  const onSubmit = async () => {
    if(!dataChanged) return;
    setLoadingPage(true);
    try {
      const res: AxiosResponse = await settingApis.updatePermissionConfig(dataChanged);
      if(res?.data?.data) {
        dispatch(settingServiceActions.refreshPermissionConfig(res.data.data));
      }
    } catch (err: any) {
        message.error(err?.response?.data?.message || 'Cập nhật quyền thất bại');
    } finally {
      setLoadingPage(false);
    }
  };

  const disableSave = () => {
    if(!dataChanged) return true;
    return JSON.stringify(initData) === JSON.stringify(dataChanged);
  };

  const onReset = () => {
    if(!initData) return;
    dispatch(settingServiceActions.updatePermissionConfigChanged(initData));
  };

  return (
    <Container>
      <Table<IPermissionConfigRes>
        rowKey='key'
        className='permission-table'
        columns={columns}
        dataSource={data ?? []}
        scroll={{
          y: 'calc(100vh - 307px)'
        }}
        pagination={false}
        bordered
      />
      <div className="card-footer">
        <Button width='10rem' height={3} ntype='SECONDARY' onClick={onReset}>Reset</Button>
        <Button width='10rem' height={3} ntype='PRIMARY' disabled={disableSave()} onClick={onSubmit}>Save</Button>
      </div>
    </Container>
  );
};

export default PermissionTable;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  .card-footer {
    display: flex;
    position: fixed;
    background-color: #FFFFFF;
    gap: 16px;
    align-items: center;
    justify-content: flex-end;
    bottom: 0px;
    right: 0px;
    left: 552px;
    box-shadow: 0px -4px 8px 0px #00000026;
    width: auto;
    padding: 16px;
    z-index: 10;
  }

  .ant-table-header {
    colgroup {
      col {
        width: calc(100% / 7) !important;
      }

      & > col:last-child {
        width: 0px !important;
      }
    }
  }

  .ant-table-wrapper .ant-table-tbody >tr >td {
    border-color: ${colorTheme['v2_border_2']} ;
    padding: 0px !important;
  }

  .ant-table-measure-row {
    display: none;
  }

  .ant-table-cell {
    border-inline-end: none !important;
  }

  .ant-table-tbody>tr>td {
    padding: 8px 0px !important;
  }

  .ant-table-cell-fix-left {
    border-inline-end: 1px solid ${colorTheme['v2_border_2']} !important;
    background: transparent;
  }

  .ant-table-wrapper .ant-table-thead {
    background: ${colorTheme['v2_neutral_2']};
  }
`;

const PermissionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  & > div {
    height: 40px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid ${colorTheme['v2_border_2']};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const Row = styled(Box)`
`;